import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import {
  deletePayrollMonthInputApi,
  DpPayrollMonthInput,
  DpPayrollMonthInputContext,
  getPayrollMonthInputContextApi,
  PayrollMonthInputPayload,
  upsertPayrollMonthInputApi,
} from "../api/payrollApi";
import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import { formatDateOnly } from "../utils/format";

/** Os campos da apuração, venham da competência ou do demonstrativo de rescisão. */
type MonthInputValues = Pick<
  DpPayrollMonthInput,
  | "workedDays"
  | "absenceDays"
  | "dsrLostCount"
  | "overtimeWorkdayHours"
  | "overtimeRestDayHours"
  | "nightHours"
  | "lateMinutes"
  | "notes"
> &
  Partial<Pick<DpPayrollMonthInput, "createdBy" | "updatedAt" | "cashAdvanceAmount" | "cashAdvanceInstallments">>;

/**
 * Dono da apuração quando não é a competência da folha: o demonstrativo de rescisão
 * (F2c) lê o contexto do mês do afastamento e grava a apuração nele mesmo.
 */
export type MonthInputSource = {
  loadContext: () => Promise<DpPayrollMonthInputContext>;
  save: (payload: PayrollMonthInputPayload) => Promise<void>;
  remove: () => Promise<void>;
  /** Mensagem depois de salvar (padrão: "Clique em Calcular para aplicar"). */
  savedMessage?: string;
};

type Props = {
  token: string;
  /** Folha do mês: a competência e o contrato. Sem `source`, são obrigatórios. */
  periodId?: string;
  contractId?: string;
  source?: MonthInputSource;
  employeeName: string;
  current: MonthInputValues | null;
  onClose: () => void;
  /** `null` = apuração excluída (ou gravada por um `source`). */
  onSaved: (input: DpPayrollMonthInput | null) => void;
};

const toField = (value: string | number | null | undefined) =>
  value === null || value === undefined ? "" : String(Number(value));
const toNumberOrNull = (value: string) => (value.trim() === "" ? null : Number(value));

const brl = (value: number | string) =>
  Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const mm = (year: number, month: number) => `${String(month).padStart(2, "0")}/${year}`;

/** Parcelas no centavo, a última com a sobra — a mesma divisão do backend (`cash-advance.rules.ts`). */
const splitInstallments = (amount: number, installments: number) => {
  const cents = Math.round(amount * 100);
  const each = Math.floor(cents / installments);
  return Array.from({ length: installments }, (_, i) => (i === installments - 1 ? cents - each * (installments - 1) : each) / 100);
};

const UNPAID_LABEL: Record<string, string> = {
  FERIAS: "Férias",
  INSS: "Afastamento pelo INSS",
  SEM_REMUNERACAO: "Afastamento sem remuneração",
};

/**
 * "Apurar mês": a revisão do DP sobre o mês do colaborador enquanto o ponto não
 * existe. O sistema mostra o que já sabe (escala, férias, afastamentos, dias
 * trabalhados derivados); o DP confirma ou corrige. Tudo vazio = mês sem
 * ocorrências. O resultado sai como "Apuração manual".
 */
export default function MonthInputModal({ token, periodId, contractId, source, employeeName, current, onClose, onSaved }: Props) {
  const [form, setForm] = useState({
    workedDays: toField(current?.workedDays),
    absenceDays: toField(current?.absenceDays),
    dsrLostCount: toField(current?.dsrLostCount),
    overtimeWorkdayHours: toField(current?.overtimeWorkdayHours),
    overtimeRestDayHours: toField(current?.overtimeRestDayHours),
    nightHours: toField(current?.nightHours),
    lateMinutes: toField(current?.lateMinutes),
    notes: current?.notes ?? "",
    cashAdvanceAmount: toField(current?.cashAdvanceAmount),
    cashAdvanceInstallments: current?.cashAdvanceInstallments ? String(current.cashAdvanceInstallments) : "1",
  });
  const [context, setContext] = useState<DpPayrollMonthInputContext | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  // O vale-adiantamento é da folha mensal: o demonstrativo de rescisão (`source`) só desconta o saldo.
  const withCashAdvance = !source;

  useEffect(() => {
    (source ? source.loadContext() : getPayrollMonthInputContextApi(token, periodId!, contractId!))
      .then(setContext)
      .catch((error: any) => toast.error(error?.message || "Erro ao carregar o que o sistema sabe do mês."));
    // `source` é recriado a cada render do pai: o contexto é lido uma vez, na abertura.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, periodId, contractId]);

  const set = (key: keyof typeof form, value: string) => setForm((f) => ({ ...f, [key]: value }));

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload: PayrollMonthInputPayload = {
      workedDays: toNumberOrNull(form.workedDays),
      absenceDays: toNumberOrNull(form.absenceDays),
      dsrLostCount: toNumberOrNull(form.dsrLostCount),
      overtimeWorkdayHours: toNumberOrNull(form.overtimeWorkdayHours),
      overtimeRestDayHours: toNumberOrNull(form.overtimeRestDayHours),
      nightHours: toNumberOrNull(form.nightHours),
      lateMinutes: toNumberOrNull(form.lateMinutes),
      notes: form.notes.trim() || null,
    };
    if (withCashAdvance) {
      const advance = toNumberOrNull(form.cashAdvanceAmount);
      payload.cashAdvanceAmount = advance;
      payload.cashAdvanceInstallments = advance ? Number(form.cashAdvanceInstallments) : null;
    }
    setSaving(true);
    try {
      if (source) {
        await source.save(payload);
        toast.success(source.savedMessage ?? "Apuração confirmada. Clique em Calcular para aplicar.");
        onSaved(null);
      } else {
        const saved = await upsertPayrollMonthInputApi(token, periodId!, contractId!, payload);
        toast.success("Apuração confirmada. Clique em Calcular para aplicar.");
        onSaved(saved);
      }
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar a apuração.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setConfirmRemove(false);
    setSaving(true);
    try {
      if (source) await source.remove();
      else await deletePayrollMonthInputApi(token, periodId!, contractId!);
      toast.success(source?.savedMessage ? "Apuração excluída." : "Apuração excluída. Clique em Calcular para aplicar.");
      onSaved(null);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao excluir a apuração.");
    } finally {
      setSaving(false);
    }
  };

  const advanceAmount = toNumberOrNull(form.cashAdvanceAmount);
  const advanceInstallments = Number(form.cashAdvanceInstallments) || 1;
  const advanceParts = advanceAmount && advanceAmount > 0 ? splitInstallments(advanceAmount, advanceInstallments) : [];
  const advancesDue = context?.cashAdvancesDue ?? [];

  const derivedWorkedDays =
    context === null ? null : Math.max(0, context.derivedWorkedDays - (toNumberOrNull(form.absenceDays) ?? 0));

  const number = (id: keyof typeof form, label: string, hint: string, step = "1", max?: number, placeholder?: string) => (
    <div className="dpFormGroup">
      <label htmlFor={`mi-${id}`}>{label}</label>
      <input
        id={`mi-${id}`}
        type="number"
        step={step}
        min="0"
        max={max}
        className="dpInput"
        placeholder={placeholder}
        value={form[id]}
        onChange={(e) => set(id, e.target.value)}
        disabled={saving}
      />
      <span className="dpFormHint">{hint}</span>
    </div>
  );

  return (
    <div className="dpModalOverlay">
      <div className="dpModal">
        <header className="dpModal__header">
          <h2 className="dpModal__title">Apurar mês — {employeeName}</h2>
          <button className="dpModal__close" onClick={onClose} disabled={saving}>
            &times;
          </button>
        </header>
        <form onSubmit={save}>
          <div className="dpModal__body">
            <div className="dpNotice" style={{ margin: 0 }}>
              <strong>O que o sistema já sabe deste mês</strong>
              {context === null ? (
                <div className="dpFormHint">Carregando...</div>
              ) : (
                <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                  <li>
                    {context.scaleSource === "ESCALA" ? "Escala publicada da loja" : "Escala padrão (segunda a sábado, sem feriados)"}: <strong>{context.scaleDays} dias</strong> no período do contrato
                    {context.scaleDays !== context.businessDays
                      ? ` (${context.businessDays} no mês civil — admissão ou rescisão no meio do mês)`
                      : ""}
                    ; {context.restDays} domingos/feriados.
                  </li>
                  {context.paidLeaves.map((l, i) => (
                    <li key={`paid-${i}`}>
                      Afastamento justificado pago pela empresa (atestado, licença): {formatDateOnly(l.startDate)} a{" "}
                      {formatDateOnly(l.endDate)} — {l.days} dia(s). <strong>Não desconta nada</strong>; não lance como falta.
                    </li>
                  ))}
                  {context.unpaid.length === 0 ? (
                    <li>Nenhuma férias nem afastamento não remunerado registrados no mês.</li>
                  ) : (
                    context.unpaid.map((u, i) => (
                      <li key={i}>
                        {UNPAID_LABEL[u.reason] ?? u.reason}: {formatDateOnly(u.startDate)} a {formatDateOnly(u.endDate)} —{" "}
                        <strong>{u.days} dia(s)</strong> de escala.
                      </li>
                    ))
                  )}
                  <li>
                    Dias trabalhados que o sistema assume: <strong>{derivedWorkedDays}</strong>
                    {toNumberOrNull(form.absenceDays) ? ` (${context.derivedWorkedDays} − faltas)` : ""}. Corrija abaixo se
                    for diferente.
                  </li>
                </ul>
              )}
            </div>
            <p className="dpFormHint" style={{ margin: 0 }}>
              Confirme o mês como está ou corrija os totais. Tudo em branco = mês sem ocorrências. O resultado sai como{" "}
              <strong>Apuração manual</strong>.
            </p>
            <div className="dpFormGrid dpFormGrid--two">
              {number("workedDays", "Dias trabalhados", "Para VT e VR. Vazio = o que o sistema assume.", "1", 31, derivedWorkedDays === null ? undefined : String(derivedWorkedDays))}
              {number("absenceDays", "Faltas (dias)", "Só injustificadas. Atestado e afastamento justificado não entram aqui — não descontam.", "1", 31)}
              {number("dsrLostCount", "DSR perdidos", "Um por semana (segunda a domingo) com falta, mesmo com várias faltas na semana. Nunca mais que as faltas.", "1", 6)}
              {number("lateMinutes", "Atrasos (minutos)", "Atrasos e saídas antecipadas somados.", "1")}
              {number("overtimeWorkdayHours", "Horas extras 50% (h)", "Em dia de trabalho. Ex.: 42,2.", "0.0001")}
              {number("overtimeRestDayHours", "Horas extras 100% (h)", "Em domingo ou feriado.", "0.0001")}
              {number("nightHours", "Adicional noturno (h)", "Horas noturnas já computadas (hora de 52m30s).", "0.0001")}
            </div>
            {withCashAdvance ? (
              <fieldset className="dpFieldset">
                <legend>Vale-adiantamento</legend>
                {advancesDue.length > 0 ? (
                  <div className="dpNotice" style={{ margin: 0 }}>
                    <strong>Parcelas de vales anteriores neste mês</strong> (descontadas no Calcular)
                    <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                      {advancesDue.map((d) => (
                        <li key={`${d.originYear}-${d.originMonth}`}>
                          Parcela {d.number}/{d.of} do vale de {mm(d.originYear, d.originMonth)}: <strong>{brl(d.amount)}</strong>
                        </li>
                      ))}
                    </ul>
                    <span className="dpFormHint">Para mudar, edite o vale na apuração do mês em que ele foi dado.</span>
                  </div>
                ) : null}
                <div className="dpFormGrid dpFormGrid--two">
                  <div className="dpFormGroup">
                    <label htmlFor="mi-cashAdvanceAmount">Vale dado neste mês (R$)</label>
                    <input
                      id="mi-cashAdvanceAmount"
                      type="number"
                      step="0.01"
                      min="0"
                      className="dpInput"
                      placeholder="Sem vale"
                      value={form.cashAdvanceAmount}
                      onChange={(e) => set("cashAdvanceAmount", e.target.value)}
                      disabled={saving}
                    />
                    <span className="dpFormHint">Descontado sem INSS, IR nem FGTS. Vazio = sem vale.</span>
                  </div>
                  <div className="dpFormGroup">
                    <label htmlFor="mi-cashAdvanceInstallments">Parcelas</label>
                    <select
                      id="mi-cashAdvanceInstallments"
                      className="dpInput"
                      value={form.cashAdvanceInstallments}
                      onChange={(e) => set("cashAdvanceInstallments", e.target.value)}
                      disabled={saving || !advanceAmount}
                    >
                      {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
                        <option key={n} value={n}>
                          {n === 1 ? "À vista (1x)" : `${n}x`}
                        </option>
                      ))}
                    </select>
                    <span className="dpFormHint">A 1ª neste mês; as demais já vêm preenchidas nos meses seguintes.</span>
                  </div>
                </div>
                {advanceParts.length > 1 && context ? (
                  <span className="dpFormHint">
                    {advanceParts
                      .map((value, i) => {
                        const index = context.year * 12 + context.month - 1 + i;
                        return `${mm(Math.floor(index / 12), (index % 12) + 1)}: ${brl(value)}`;
                      })
                      .join(" · ")}
                    . Na rescisão, o saldo é descontado de uma vez.
                  </span>
                ) : null}
              </fieldset>
            ) : null}
            <div className="dpFormGroup">
              <label htmlFor="mi-notes">Observação</label>
              <textarea
                id="mi-notes"
                className="dpTextarea"
                maxLength={500}
                placeholder="Ex.: conforme folha de ponto em papel."
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                disabled={saving}
              />
            </div>
            {current?.createdBy && current.updatedAt ? (
              <span className="dpFormHint">
                Apuração registrada por {current.createdBy.nomeCompleto} em{" "}
                {new Date(current.updatedAt).toLocaleString("pt-BR")}.
              </span>
            ) : null}
          </div>
          <footer className="dpModal__footer">
            {current ? (
              <button type="button" className="dpBtn dpBtn--danger" onClick={() => setConfirmRemove(true)} disabled={saving} style={{ marginRight: "auto" }}>
                Excluir apuração
              </button>
            ) : null}
            <button type="button" className="dpBtn dpBtn--secondary" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className="dpBtn dpBtn--primary" disabled={saving || context === null}>
              {current ? "Salvar" : "Confirmar apuração"}
            </button>
          </footer>
        </form>
      </div>
      <EcoConfirmDialog
        open={confirmRemove}
        title="Excluir a apuração?"
        message={`A apuração de ${employeeName}${current?.cashAdvanceAmount ? " e o vale-adiantamento dela" : ""} serão excluídos. O colaborador volta a "sem apuração" ao recalcular.`}
        confirmLabel="Excluir"
        tone="danger"
        busy={saving}
        onConfirm={remove}
        onCancel={() => setConfirmRemove(false)}
      />
    </div>
  );
}
